import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { stripLangPrefix, langPath } from '../utils/langPath';

/**
 * Switch de langue qui met à jour à la fois le context React (LanguageContext)
 * ET l'URL affichée dans la barre du navigateur. Préserve le path courant.
 *
 * Usage typique : dans Header / Footer / page languages selector
 *   const switchLanguage = useSwitchLanguage();
 *   <button onClick={() => switchLanguage('en')}>EN</button>
 *
 * Comportement :
 * - "/fr/about" + switchLanguage('en')  → navigate("/en/about")
 * - "/dashboard/ppl" + switchLanguage('en') → pas de navigate (route auth, langue gérée par profil)
 *   mais setLanguage('en') est quand même appelé
 *
 * toggle() retourne un helper qui inverse fr↔en pratique pour les boutons toggle.
 */
export function useSwitchLanguage() {
    const { language, setLanguage } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();

    return useCallback((newLang) => {
        if (newLang !== 'fr' && newLang !== 'en') return;
        setLanguage(newLang);

        // Strip le préfixe lang existant puis re-préfixe avec la nouvelle langue
        // (langPath gère les zones sans préfixe : /dashboard, /admin, etc.)
        const cleanPath = stripLangPrefix(location.pathname);
        const newPath = langPath(cleanPath, newLang);

        // Évite un navigate inutile si le path est inchangé (cas zones non-préfixées)
        if (newPath !== location.pathname) {
            navigate(newPath + location.search + location.hash, { replace: true });
        }
    }, [language, setLanguage, navigate, location]);
}

/**
 * Toggle pratique fr ↔ en, basé sur la langue actuelle.
 */
export function useToggleLanguage() {
    const { language } = useLanguage();
    const switchLanguage = useSwitchLanguage();
    return useCallback(() => {
        switchLanguage(language === 'fr' ? 'en' : 'fr');
    }, [language, switchLanguage]);
}
